import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const SUPPORTED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const ACCEPTED_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];

function getExtension(filename) {
  return filename.split('.').pop().toLowerCase();
}

function isSupportedFile(file) {
  if (!file) return false;
  const extension = getExtension(file.name);
  return SUPPORTED_TYPES.includes(file.type) || ACCEPTED_EXTENSIONS.includes(extension);
}

function DocumentUpload() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [storedDocument, setStoredDocument] = useState(null);
  const navigate = useNavigate();
  const activeStep = 1;
  const steps = [
    { number: 1, title: 'Upload Document', description: 'Attach the required file for your application' },
    { number: 2, title: 'Applicant Information', description: 'Provide applicant contact and address details' },
    { number: 3, title: 'Project Information', description: 'Describe the project and funding needs' },
    { number: 4, title: 'Budget', description: 'Provide budget allocations' },
    { number: 5, title: 'DOL Determination', description: 'Complete final eligibility review' },
  ];

  useEffect(() => {
    const storedData = sessionStorage.getItem('newApplicationData');
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        // Require a selected program to proceed with creating an application
        if (!parsed.program) {
          alert('Please select a program from the Dashboard before starting a new application.');
          navigate('/dashboard');
          return;
        }
        setStoredDocument(parsed.document || null);
      } catch {
        sessionStorage.removeItem('newApplicationData');
      }
    } else {
      alert('Please select a program from the Dashboard before starting a new application.');
      navigate('/dashboard');
    }
  }, [navigate]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = (event) => {
    setUploadError('');
    const file = event.target.files[0];
    if (!file) {
      setSelectedFile(null);
      setPreviewUrl('');
      return;
    }

    if (!isSupportedFile(file)) {
      setUploadError('Unsupported file type. Upload PDF, Word, or Excel files only.');
      setSelectedFile(null);
      setPreviewUrl('');
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setSelectedFile(file);
    setPreviewUrl(objectUrl);
  };

  const handleSaveAndContinue = () => {
    setUploadError('');
    const fileToSave = selectedFile || storedDocument;

    const currentData = JSON.parse(sessionStorage.getItem('newApplicationData') || '{}');
    setIsSaving(true);
    const reader = new FileReader();

    reader.onload = () => {
      sessionStorage.setItem(
        'newApplicationData',
        JSON.stringify({
          ...currentData,
          document: {
            name: fileToSave.name,
            type: fileToSave.type || 'application/octet-stream',
            size: fileToSave.size,
            data: reader.result,
          },
        })
      );
      navigate('/dashboard/new-application');
    };

    reader.onerror = () => {
      setUploadError('Unable to read the selected file. Please try again.');
      setIsSaving(false);
    };

    if (fileToSave instanceof File) {
      reader.readAsDataURL(fileToSave);
    } else {
      // Stored document is already a data URL and may be in shared session
      sessionStorage.setItem(
        'newApplicationData',
        JSON.stringify({
          ...currentData,
          document: storedDocument,
        })
      );
      navigate('/dashboard/new-application');
    }
  };

  const handleRemove = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    setStoredDocument(null);
    const currentData = JSON.parse(sessionStorage.getItem('newApplicationData') || '{}');
    delete currentData.document;
    sessionStorage.setItem('newApplicationData', JSON.stringify(currentData));
  };


  const activeDocument = selectedFile || storedDocument;
  const downloadHref = selectedFile ? previewUrl : storedDocument?.data;
  const canPreviewPdf = activeDocument?.type === 'application/pdf';

  return (
    <div className="page-stack">
        <section className="app-flow-header">
          <div>
            <p className="app-flow-tag">Application Flow</p>
            <h1>Document Upload</h1>
            <p className="app-flow-description">
              Upload your application file here. Supported files include PDF, Word, and Excel.
            </p>
          </div>
          <div className="step-badges">
            {steps.map((step) => (
              <div key={step.number} className={`step-item ${activeStep === step.number ? 'active' : ''}`}>
                <div className="step-number">{step.number}</div>
                <div className="step-text">
                  <div className="step-title">{step.title}</div>
                  <div className="step-desc">{step.description}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="application-content">
          <div className="form-card">
            <h2 className="section-heading">Step 1: Upload Your Document</h2>
            <p className="form-description">
              Choose a document and preview your uploaded file before moving to the next step.
            </p>

            <div className="upload-section">
              <label className="form-field upload-field">
                <span>Select document</span>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  onChange={handleFileChange}
                />
              </label>

              {uploadError && <div className="field-error">{uploadError}</div>}

              {activeDocument && (
                <div className="document-summary">
                  <div className="document-summary-header">Selected document</div>
                  <div className="document-preview-row">
                    <span>{activeDocument.name}</span>
                    <button type="button" className="link-btn" onClick={() => window.open(downloadHref)}>
                      View/download
                    </button>
                    <button type="button" className="link-btn" onClick={handleRemove}>
                      Remove
                    </button>
                  </div>
                </div>
              )}

              {canPreviewPdf && downloadHref && (
                <div className="preview-frame">
                  <object data={downloadHref} type="application/pdf" width="100%" height="520">
                    <p>Preview is unavailable. Use the download link above.</p>
                  </object>
                </div>
              )}

              {!canPreviewPdf && activeDocument && (
                <div className="preview-placeholder">
                  <p>Preview is not available for this file type.</p>
                  <p>You can still download it and continue.</p>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="button" className="cancel-btn" onClick={() => navigate('/dashboard')}>
                Cancel
              </button>
              <button type="button" className="submit-btn" onClick={handleSaveAndContinue} disabled={isSaving}>
                {isSaving ? 'Saving document...' : 'Upload and Continue'}
              </button>
            </div>
          </div>
        </section>
    </div>
  );
}

export default DocumentUpload;
